import { useId, forwardRef, Ref } from 'react';
import Skeleton from 'react-loading-skeleton';
import { MarginProps } from '../base-margin/base-margin.component';
import {
  FieldContainer,
  InfoMessageText,
  InfoTextContainer,
  InputContainer,
} from './input.styles';
import FieldLabel from './field-label.component';
import Input from './input.component';
import InputMessage, {
  InputMessageType,
} from '../input-message/input-message.component';
import IncrementControls from './input-delta.component';

interface Props {
  isOptional?: boolean;
  name: string;
  placeholder?: string;
  label: string;
  isHidden?: boolean;
  isLabelHidden?: boolean;
  type?: React.HTMLInputTypeAttribute;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  hint?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  maxLength?: number;
  disabled?: boolean;
  $borderNone?: boolean;
  showValidationError?: boolean;
  showInfoMessage?: boolean;
  infoMessageType?: InputMessageType;
  infoMessage?: string;
  value?: string;
  errorMessage?: string;
  optionalText?: string;
  prefix?: {
    element: React.ReactNode;
    isClickable?: boolean;
  };
  suffix?: {
    element: React.ReactNode;
    isClickable?: boolean;
  };
  labelEndElement?: React.ReactNode;
  autoComplete?: string;
  showSkeleton?: boolean;
  skeletonClassName?: string;
  readOnly?: boolean;
  errorColor?: string;
  tabIndex?: number;
  incrementControls?: boolean;
  minValue?: number;
  maxValue?: number;
  useFlagEmojiFont?: boolean;
  showMaxLengthCounter?: boolean;
}

const FormField = (
  {
    name,
    placeholder,
    label,
    type = 'text',
    $borderNone,
    inputMode,
    isHidden,
    isLabelHidden,
    hint,
    maxLength,
    disabled = false,
    onChange,
    onFocus,
    onBlur,
    showValidationError = true,
    showInfoMessage = false,
    infoMessageType = 'alertTriangleYellow',
    infoMessage,
    isOptional,
    value,
    errorMessage,
    optionalText = 'Optional',
    prefix,
    suffix,
    labelEndElement,
    autoComplete,
    showSkeleton,
    skeletonClassName,
    readOnly,
    errorColor,
    tabIndex,
    incrementControls,
    useFlagEmojiFont,
    minValue = 0,
    maxValue = Number.MAX_SAFE_INTEGER,
    showMaxLengthCounter,
  }: Props & MarginProps,
  ref: Ref<HTMLInputElement>
) => {
  const id = useId();
  const inputId = name + id;
  const isInvalid = errorMessage !== undefined;

  const deltaIncrement = incrementControls && (
    <IncrementControls
      onIncrement={() => {
        const parsedValue = Number(value) || 0;
        if (parsedValue === maxValue) return;
        onChange?.({
          target: { name, value: String(parsedValue + 1) },
        } as any);
      }}
      onDecrement={() => {
        const parsedValue = Number(value) || 0;
        if (parsedValue === minValue) return;
        onChange?.({
          target: { name, value: String(parsedValue - 1) },
        } as any);
      }}
      disabled={disabled}
      readOnly={readOnly}
    />
  );

  return (
    <FieldContainer
      $isHidden={isHidden}
      $isIncrementControls={incrementControls}>
      <InputContainer>
        <FieldLabel
          isHidden={isLabelHidden}
          isOptional={isOptional}
          optionalText={optionalText}
          labelEndElement={labelEndElement}
          hint={hint}
          inputId={inputId}>
          {label}
        </FieldLabel>
        {showSkeleton ? (
          <Skeleton
            width='100%'
            height='4rem'
            borderRadius={10}
            className={skeletonClassName}
          />
        ) : (
          <>
            {deltaIncrement}
            <Input
              autoComplete={autoComplete}
              ref={ref}
              type={type}
              id={inputId}
              placeholder={placeholder}
              required={!isOptional}
              inputMode={inputMode}
              name={name}
              disabled={disabled}
              value={value}
              maxLength={maxLength}
              onChange={onChange}
              onFocus={onFocus}
              onBlur={onBlur}
              isInvalid={isInvalid}
              prefix={prefix}
              suffix={suffix}
              tabIndex={tabIndex}
              readOnly={readOnly}
              $borderNone={$borderNone}
              useFlagEmojiFont={useFlagEmojiFont}
            />
          </>
        )}
      </InputContainer>

      {showValidationError && isInvalid && !!errorMessage && (
        <InputMessage
          type='error'
          color={errorColor}>
          {errorMessage}
        </InputMessage>
      )}
      {showInfoMessage && (
        <InputMessage type={infoMessageType}>
          <InfoMessageText>{infoMessage}</InfoMessageText>
        </InputMessage>
      )}

      {showMaxLengthCounter && (
        <InfoTextContainer $mt='6px'>
          {value?.length ?? 0} / {maxLength}
        </InfoTextContainer>
      )}
    </FieldContainer>
  );
};

export default forwardRef(FormField);
